import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, UserPlus, X, Check } from "lucide-react";
import T from "@/components/T";

export default function RecruitView({ ch, setCh }) {
  const [posts, setPosts] = useState([]);
  const [myGuild, setMyGuild] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setPosts(await api.recruitPosts());
    setMyGuild(await api.guildMine());
  };
  useEffect(() => { refresh(); }, []);

  const iLead = myGuild && myGuild.leader_id === ch.user_id;
  const myPost = posts.find(p => p.leader_id === ch.user_id);

  const post = async () => {
    if (!message.trim() || busy) return;
    setBusy(true);
    try {
      await api.recruitPost(message);
      setMessage("");
      await refresh();
      toast.success("Banner posted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const apply = async (id) => {
    setBusy(true);
    try {
      await api.recruitApply(id);
      await refresh();
      toast.success("Application sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const decide = async (pid, aid, accept) => {
    setBusy(true);
    try {
      await api.recruitDecide(pid, aid, accept);
      await refresh();
      if (accept) {
        const c = await api.character();
        setCh(c);
      }
      toast(accept ? "Accepted into the guild" : "Rejected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const close = async (pid) => {
    setBusy(true);
    try { await api.recruitClose(pid); await refresh(); toast("Banner taken down"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      {iLead && (
        <div className="panel p-3 space-y-2" data-testid="recruit-post-form">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-[#E55A00]" strokeWidth={1.5} />
            <T>Post Recruitment Banner</T>
          </div>
          {myPost ? (
            <div className="panel-inner p-2">
              <div className="italic text-xs text-[#B0ADA5]">&ldquo;{myPost.message}&rdquo;</div>
              <div className="font-mono text-[10px] text-[#8c8c8c] mt-1">
                {myPost.applications?.length || 0} applicants
              </div>
              <div className="mt-2 space-y-1">
                {(myPost.applications || []).map(a => (
                  <div key={a.user_id} className="flex justify-between items-center border border-[#262626] p-1">
                    <div className="text-xs">
                      <span className="text-[#E2DFD8]">{a.name}</span>
                      <span className="text-[#8c8c8c] ml-2 font-mono">Lv{a.level} · {a.class}</span>
                      {a.appeal >= 10 && <span className="ml-1 text-[#B89C3A] text-[10px]">(charismatic)</span>}
                    </div>
                    <div className="flex gap-1">
                      <button
                        data-testid={`recruit-accept-${a.user_id}`}
                        onClick={() => decide(myPost.post_id, a.user_id, true)}
                        className="px-2 py-0.5 border border-[#4A5D23] text-[#6E8B3D]"
                      ><Check className="w-3 h-3" strokeWidth={2} /></button>
                      <button
                        data-testid={`recruit-reject-${a.user_id}`}
                        onClick={() => decide(myPost.post_id, a.user_id, false)}
                        className="px-2 py-0.5 border border-[#8A0303] text-[#E33333]"
                      ><X className="w-3 h-3" strokeWidth={2} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => close(myPost.post_id)}
                className="mt-2 w-full py-1 border border-[#333] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase"
              >
                Take Down Banner
              </button>
            </div>
          ) : (
            <>
              <textarea
                data-testid="recruit-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Why should heroes join your ranks?"
                maxLength={280}
                rows={3}
                className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
              />
              <button
                data-testid="recruit-post-btn"
                onClick={post}
                disabled={busy || !message.trim()}
                className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
              >
                Nail the Banner
              </button>
            </>
          )}
        </div>
      )}

      <div className="panel p-2" data-testid="recruit-board">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1">
          Recruitment Board ({posts.length})
        </div>
        {posts.length === 0 && (
          <p className="italic text-[#666] text-xs px-2 py-2">Nobody is recruiting right now.</p>
        )}
        {posts.map(p => (
          <div key={p.post_id} className="panel-inner p-2 mb-1" data-testid={`recruit-post-${p.post_id}`}>
            <div className="font-heading text-sm text-[#E2DFD8]">
              «{p.guild_name}» <span className="text-[#8c8c8c] text-xs">[{p.guild_tag}]</span>
            </div>
            <div className="italic text-xs text-[#B0ADA5] mt-1">&ldquo;{p.message}&rdquo;</div>
            <div className="flex justify-between items-center mt-2">
              <span className="font-mono text-[10px] text-[#666]">{p.applicant_count} applicants</span>
              {!myGuild && p.leader_id !== ch.user_id && (
                <button
                  data-testid={`recruit-apply-${p.post_id}`}
                  onClick={() => apply(p.post_id)}
                  disabled={busy}
                  className="px-2 py-1 border border-[#4A5D23] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1"
                >
                  <UserPlus className="w-3 h-3" strokeWidth={1.5} /> Apply
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
